import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers, updateUser, clearFlags } from '../../store/slices/userSlice';
import EditUserModal from '../../components/ui/modals/EditUserModal';
import CreateUserModal from '../../components/ui/modals/CreateUserModal';
import {
  Card, Typography, Space, Row, Col,
  EditOutlined, CheckOutlined, CloseOutlined, PlusOutlined,
  CustomButton, CustomInput, CustomSelect, CustomTable,
  StatusBadge, CustomLoader, CustomModal,
  Colors, Constants, showToast, removeUnderScoreAndCapitalize,
  getDateFromDateString
} from '../../utils/commonImports'

const { Title } = Typography;

const Users = () => {
  const dispatch = useDispatch();
  const { users, pagination, loading, isUpdated, isCreated, error } = useSelector((state) => state.user);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(Constants.ITEMS_PER_PAGE);
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Filter states
  const initialFilters = {
    email: '',
    name: '',
    contactNumber: '',
    role: '',
  }
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    const filterParams = {
      page: currentPage,
      perPage: limit,
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
    };
    
    dispatch(getAllUsers(filterParams));
  }, [dispatch, currentPage, limit, filters]);

  useEffect(() => {
    if (isUpdated || isCreated) {
      setLoadingUserId(null);
      dispatch(clearFlags());
      dispatch(getAllUsers({ page: currentPage, perPage: limit }));
    }

    if (error) {
      showToast(error, 'error')
    }
  }, [isUpdated, isCreated, dispatch, currentPage, limit, error]);

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => (currentPage - 1) * limit + index + 1,
      width: 50
    },
    {
      title: 'Name',
      dataIndex: 'firstName',
      key: 'name',
      render: (text, record) => `${record.firstName} ${record.lastName || ''}`,
      width: 150
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200
    },
    {
      title: 'Contact Number',
      dataIndex: 'contactNumber',
      key: 'contactNumber',
      width: 120
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (text) => <StatusBadge status={text} type="role" />,
      width: 100
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text) => <StatusBadge status={text} type="status" />,
      width: 100
    },
    {
      title: 'Can Approve',
      dataIndex: 'can_approve',
      key: 'can_approve',
      render: (value, record) => {
        // Only show can_approve for admin users
        if (record.role === Constants.ROLES.ADMIN) {
          return (
            <StatusBadge 
              status={value ? 'Yes' : 'No'} 
              type="canApprove"
            />
          );
        }
        return <span style={{ color: Colors.gray }}>-</span>;
      },
      width: 100
    },
    {
      title: 'Created On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => getDateFromDateString(text),
      width: 120
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <CustomButton 
            onClick={() => handleEdit(record._id)}
            width="auto"
            height={28}
            fontSize={10}
          >
            <EditOutlined /> Edit
          </CustomButton>
          <CustomButton
            onClick={() => handleStatusToggle(record)}
            disabled={loadingUserId === record._id}
            width="auto"
            height={28}
            fontSize={10}
            style={{
              backgroundColor: record.status === Constants.STATUS.ACTIVE ? Colors.error : Colors.primary,
              borderColor: record.status === Constants.STATUS.ACTIVE ? Colors.error : Colors.primary
            }}
          >
            {loadingUserId === record._id ? (
              <>
                <CustomLoader size="small" />
                {record.status === Constants.STATUS.ACTIVE ? ' Deactivating...' : ' Activating...'}
              </>
            ) : (
              <>
                {record.status === Constants.STATUS.ACTIVE ? <CloseOutlined /> : <CheckOutlined />}
                {record.status === Constants.STATUS.ACTIVE ? ' Deactivate' : ' Activate'}
              </>
            )}
          </CustomButton>
        </Space>
      ),
      width: 150
    }
  ];

  const handleTableChange = (paginationInfo) => {
    setCurrentPage(paginationInfo.current);
    setLimit(paginationInfo.pageSize);
  };

  const handleEdit = (id) => {
    const user = users.find(u => u._id === id);
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedUser(null);
  };

  const handleStatusToggle = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleConfirmStatusToggle = () => {
    if (selectedUser) {
      const newStatus = selectedUser.status === Constants.STATUS.ACTIVE ? Constants.STATUS.INACTIVE : Constants.STATUS.ACTIVE;
      setLoadingUserId(selectedUser._id);
      dispatch(updateUser({ 
        id: selectedUser._id, 
        userData: { status: newStatus } 
      }));
      setModalOpen(false);
      setSelectedUser(null);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const handleCreate = () => {
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={4} style={{ margin: 0 }}>Users</Title>
        <CustomButton
          onClick={handleCreate}
          width="auto"
          height={32}
          fontSize={12}
        >
          <PlusOutlined /> Create User
        </CustomButton>
      </div>

      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col xs={12} sm={12} md={4} style={{ marginBottom: '8px' }}>
          <CustomInput
            placeholder="Search by email"
            value={filters.email}
            onChange={(e) => setFilters(prev => ({ ...prev, email: e.target.value }))}
          />
        </Col>
        <Col xs={12} sm={12} md={4} style={{ marginBottom: '8px' }}>
          <CustomInput
            placeholder="Search by name"
            value={filters.name}
            onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
          />
        </Col>
        <Col xs={12} sm={12} md={4} style={{ marginBottom: '8px' }}>
          <CustomInput
            placeholder="Search by contact"
            value={filters.contactNumber}
            onChange={(e) => setFilters(prev => ({ ...prev, contactNumber: e.target.value }))}
          />
        </Col>
        <Col xs={12} sm={12} md={4} style={{ marginBottom: '8px' }}>
          <CustomSelect
            placeholder="Select role"
            value={filters.role}
            onChange={(value) => setFilters(prev => ({ ...prev, role: value }))}
            options={[
              { value: '', label: 'All Roles' },
              { value: Constants.ROLES.ADMIN, label: removeUnderScoreAndCapitalize(Constants.ROLES.ADMIN) },
              { value: Constants.ROLES.USER, label: removeUnderScoreAndCapitalize(Constants.ROLES.USER) }
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={3} style={{ marginBottom: '8px' }}>
          <CustomButton
            color={Colors.error}
            onClick={() => setFilters(initialFilters)}
            variant="outline"
            width="100%"
            fontSize={12}
          >
            Reset Filters
          </CustomButton>
        </Col>
      </Row>
      
      <CustomTable
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey={(record) => record._id}
        currentPage={currentPage}
        total={pagination?.totalUsers || 0}
        pageSize={limit}
        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} users`}
        onChange={handleTableChange}
      />
      
      <CustomModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmStatusToggle}
        title={`${selectedUser?.status === Constants.STATUS.ACTIVE ? 'Deactivate' : 'Activate'} User`}
        content={`Are you sure you want to ${selectedUser?.status === Constants.STATUS.ACTIVE ? 'deactivate' : 'activate'} ${selectedUser?.firstName} ${selectedUser?.lastName || ''}?`}
        confirmText="Yes"
        cancelText="No"
        loading={loadingUserId === selectedUser?._id}
      />
      
      <EditUserModal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        user={selectedUser}
      />
      
      <CreateUserModal
        isOpen={createModalOpen}
        onClose={handleCloseCreateModal}
      />
    </Card>
  );
};

export default Users;